import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Modal } from 'react-bootstrap';
import { MoreHorizontal } from 'lucide-react';
import type {
  KanbanCardData,
  KanbanLabel,
  BoardMember,
  ChecklistItem,
  KanbanCardComment,
  KanbanCardCommentAttachment,
  KanbanColumn,
  KanbanCardPatchPayload,
} from 'types/kanban';
import { sanitizeCommentHtml, stripCommentHtmlImages } from 'utils/commentHtml';
import { getCommentAttachmentUploadUrl } from 'services/kanban';
import {
  insertMarkdownAroundSelection,
  insertBulletList,
  insertNumberedList,
} from 'utils/markdownEditorUtils';
import { generateId, DEFAULT_LABEL_COLORS } from '../kanbanUtils';
import {
  DEFAULT_REMINDER_LEAD_DAYS,
  datetimeLocalToIso,
  dueAtToDatetimeLocalValue,
} from '../kanbanDueDate';
import {
  formatRecurrenceLabel,
  presetToRecurrence,
  recurrenceToPreset,
  type RecurrencePresetId,
} from '../kanbanRecurrence';
import CardDetailLeftColumn from './CardDetailLeftColumn';
import CardDetailRightColumn from './CardDetailRightColumn';
import CardDetailLabelsPanel from './CardDetailLabelsPanel';
import CardDetailMembersPanel from './CardDetailMembersPanel';
import CardDetailCreateLabelModal from './CardDetailCreateLabelModal';
import CardMenu, { CardMenuView } from '../CardMenu';
import styles from './CardDetailModal.module.scss';

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export interface CardDetailModalProps {
  show: boolean;
  onHide: () => void;
  card: KanbanCardData | null;
  columnTitle: string;
  boardId: string | null;
  activeBoardId: string | null;
  boards: Array<{ id: string; title: string }>;
  companyId: string | null;
  boardLabels: KanbanLabel[];
  boardMembers: BoardMember[];
  columns: KanbanColumn[];
  /** Select a different card while keeping the modal open. */
  onSelectCard: (id: string | null) => void;
  onUpdateCard: (cardId: string, updates: KanbanCardPatchPayload) => void;
  onAddComment: (
    cardId: string,
    text: string,
    attachments?: KanbanCardCommentAttachment[],
  ) => void;
  onCreateLabel: (label: KanbanLabel) => Promise<KanbanLabel | null>;
  onUpdateLabel: (
    labelId: string,
    updates: { name?: string; color?: string },
  ) => Promise<KanbanLabel | null>;
  onDeleteLabel: (labelId: string) => Promise<void>;
  onCopyCardLink: (cardId: string) => void | Promise<void>;
  onArchiveCard: (cardId: string) => void | Promise<void>;
  onMoveCard: (
    cardId: string,
    targetColumnId: string,
    targetBoardId?: string,
    targetPosition0Based?: number,
  ) => void | Promise<void>;
  getBoardListsForMove?: (boardId: string) => Promise<KanbanColumn[]>;
  renameCardId?: string | null;
  onClearRenameRequest?: () => void;
  /** Called when comment attachment upload fails. */
  onCommentUploadError?: (message: string) => void;
  /** Comment ID to scroll to when opening from a shared link. */
  commentIdToScroll?: string | null;
  /** Called when user clicks comment timestamp to copy link. */
  onCopyCommentLink?: (
    cardId: string,
    commentId: string,
  ) => void | Promise<void>;
}

export default function CardDetailModal({
  show,
  onHide,
  card,
  columnTitle,
  boardLabels,
  boardMembers,
  columns,
  onSelectCard,
  onUpdateCard,
  onAddComment,
  onCreateLabel,
  onUpdateLabel,
  onDeleteLabel,
  onCopyCardLink,
  onArchiveCard,
  onMoveCard,
  boardId,
  activeBoardId,
  boards,
  companyId,
  getBoardListsForMove,
  renameCardId = null,
  onClearRenameRequest,
  onCommentUploadError,
  commentIdToScroll = null,
  onCopyCommentLink,
}: CardDetailModalProps) {
  const [commentText, setCommentText] = useState('');
  const [commentEditorActive, setCommentEditorActive] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState('');
  const [editingCardTitle, setEditingCardTitle] = useState(false);
  const [cardTitleDraft, setCardTitleDraft] = useState('');
  const [labelsPanelOpen, setLabelsPanelOpen] = useState(false);
  const [membersPanelOpen, setMembersPanelOpen] = useState(false);
  const [labelsPanelAnchor, setLabelsPanelAnchor] = useState<DOMRect | null>(
    null,
  );
  const [membersPanelAnchor, setMembersPanelAnchor] = useState<DOMRect | null>(
    null,
  );
  const [newChecklistItemText, setNewChecklistItemText] = useState('');
  const [labelSearch, setLabelSearch] = useState('');
  const [newLabelName, setNewLabelName] = useState('');
  const [newLabelColor, setNewLabelColor] = useState(DEFAULT_LABEL_COLORS[0]);
  const [editingLabelId, setEditingLabelId] = useState<string | null>(null);
  const [createLabelModalOpen, setCreateLabelModalOpen] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [showStartDateSection, setShowStartDateSection] = useState(false);
  const [showDueDateSection, setShowDueDateSection] = useState(false);
  const [startDateLocal, setStartDateLocal] = useState('');
  const [dueDateLocal, setDueDateLocal] = useState('');
  const [reminderLeadValueDraft, setReminderLeadValueDraft] = useState(14);
  const [reminderLeadUnitDraft, setReminderLeadUnitDraft] = useState<
    'days' | 'hours' | 'minutes' | 'seconds'
  >('days');
  const [recurrencePreset, setRecurrencePreset] =
    useState<RecurrencePresetId>('none');
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentText, setEditingCommentText] = useState('');
  /** Files selected for the next comment; uploaded only when user clicks Add. */
  const [pendingCommentFiles, setPendingCommentFiles] = useState<File[]>([]);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const isSubmittingCommentRef = useRef(false);
  const availableLabelColors = useMemo(() => {
    const editingLabel = editingLabelId
      ? boardLabels.find((l) => l.id === editingLabelId)
      : null;
    return DEFAULT_LABEL_COLORS.filter((c: string) => {
      if (
        editingLabel &&
        editingLabel.color.toLowerCase() === c.toLowerCase()
      ) {
        return true;
      }
      return !boardLabels.some(
        (l) => l.color.toLowerCase() === c.toLowerCase(),
      );
    });
  }, [boardLabels, editingLabelId]);
  const labelsBtnRef = useRef<HTMLButtonElement>(null);
  const membersBtnRef = useRef<HTMLButtonElement>(null);
  const membersAddBtnRef = useRef<HTMLButtonElement>(null);
  const descriptionTextareaRef = useRef<HTMLTextAreaElement>(null);
  const pendingDescSelectionRef = useRef<{
    selectionStart: number;
    selectionEnd: number;
  } | null>(null);
  const commentEditRef = useRef<HTMLDivElement>(null);
  const commentEditorRef = useRef<HTMLDivElement>(null);
  const cardTitleInputRef = useRef<HTMLInputElement>(null);
  const cardMenuButtonRef = useRef<HTMLButtonElement>(null);
  const [cardMenuAnchor, setCardMenuAnchor] = useState<{
    cardId: string;
    currentColumnId: string;
    rect: DOMRect;
    boundsRect?: DOMRect | null;
  } | null>(null);
  const [cardMenuView, setCardMenuView] = useState<CardMenuView>('main');
  const [closeOnBackFromMoveView, setCloseOnBackFromMoveView] = useState(false);
  const prevCardRef = useRef<{ id: string; description: string | undefined }>({
    id: '',
    description: undefined,
  });

  const applyDescMarkdownInsert = useCallback(
    (
      fn: (el: HTMLTextAreaElement) => {
        newValue: string;
        selectionStart: number;
        selectionEnd: number;
      },
    ) => {
      const el = descriptionTextareaRef.current;
      if (!el) return;
      const { newValue, selectionStart, selectionEnd } = fn(el);
      setDescriptionDraft(newValue);
      pendingDescSelectionRef.current = { selectionStart, selectionEnd };
    },
    [],
  );

  const handleDescBold = useCallback(() => {
    applyDescMarkdownInsert((el) =>
      insertMarkdownAroundSelection(el, '**', '**'),
    );
  }, [applyDescMarkdownInsert]);

  const handleDescItalic = useCallback(() => {
    applyDescMarkdownInsert((el) =>
      insertMarkdownAroundSelection(el, '_', '_'),
    );
  }, [applyDescMarkdownInsert]);

  const handleDescStrikethrough = useCallback(() => {
    applyDescMarkdownInsert((el) =>
      insertMarkdownAroundSelection(el, '~~', '~~'),
    );
  }, [applyDescMarkdownInsert]);

  const handleDescCode = useCallback(() => {
    applyDescMarkdownInsert((el) =>
      insertMarkdownAroundSelection(el, '`', '`'),
    );
  }, [applyDescMarkdownInsert]);

  const handleDescClearFormatting = useCallback(() => {
    // No-op for Markdown: optional future enhancement to strip **, _, etc.
  }, []);

  const handleDescBulletList = useCallback(() => {
    applyDescMarkdownInsert(insertBulletList);
  }, [applyDescMarkdownInsert]);

  const handleDescNumberedList = useCallback(() => {
    applyDescMarkdownInsert(insertNumberedList);
  }, [applyDescMarkdownInsert]);

  // Sync description draft when card identity or modal visibility changes, or when card description was updated from parent (e.g. after save) and we're not editing
  useEffect(() => {
    if (show && card) {
      const id = card.id;
      const description = card.description ?? '';
      const prev = prevCardRef.current;
      const cardIdChanged = prev.id !== id;
      const descriptionUpdatedFromParent =
        !editingDescription && prev.description !== description;
      if (cardIdChanged) {
        prevCardRef.current = { id, description };
        setDescriptionDraft(description);
        setEditingDescription(false);
        setShowFullDescription(false);
        setShowStartDateSection(false);
        setShowDueDateSection(false);
        setEditingCardTitle(false);
        setCommentEditorActive(false);
        setEditingCommentId(null);
        setEditingCommentText('');
      } else if (descriptionUpdatedFromParent) {
        prevCardRef.current = { id, description };
        setDescriptionDraft(description);
      }
    } else if (!show) {
      prevCardRef.current = { id: '', description: undefined };
      setShowDueDateSection(false);
      setShowStartDateSection(false);
      setEditingCardTitle(false);
      setCommentEditorActive(false);
      setEditingCommentId(null);
      setEditingCommentText('');
    }
  }, [show, card, editingDescription]);

  useEffect(() => {
    if (!show) {
      setCardMenuAnchor(null);
      setCardMenuView('main');
    }
  }, [show]);

  const prevEditingDescRef = useRef(false);
  useEffect(() => {
    const justOpened = editingDescription && !prevEditingDescRef.current;
    prevEditingDescRef.current = editingDescription;
    if (justOpened && descriptionTextareaRef.current) {
      descriptionTextareaRef.current.focus();
    }
  }, [editingDescription]);

  // After descriptionDraft updates from toolbar (MD insert), restore selection in textarea
  useEffect(() => {
    if (
      editingDescription &&
      descriptionTextareaRef.current &&
      pendingDescSelectionRef.current
    ) {
      const { selectionStart, selectionEnd } = pendingDescSelectionRef.current;
      pendingDescSelectionRef.current = null;
      descriptionTextareaRef.current.setSelectionRange(
        selectionStart,
        selectionEnd,
      );
      descriptionTextareaRef.current.focus();
    }
  }, [editingDescription, descriptionDraft]);

  useEffect(() => {
    if (show && card && renameCardId === card.id) {
      setEditingCardTitle(true);
      setCardTitleDraft(card.title);
      onClearRenameRequest?.();
    }
  }, [show, card, renameCardId, onClearRenameRequest]);

  useEffect(() => {
    if (!card) return;
    setStartDateLocal(
      card.startAt ? dueAtToDatetimeLocalValue(card.startAt) : '',
    );
    setDueDateLocal(card.dueAt ? dueAtToDatetimeLocalValue(card.dueAt) : '');
    setReminderLeadValueDraft(
      card.reminderLeadValue ?? DEFAULT_REMINDER_LEAD_DAYS,
    );
    setReminderLeadUnitDraft(card.reminderLeadUnit ?? 'days');
    const preset = recurrenceToPreset(card.recurrence);
    setRecurrencePreset(preset);
  }, [card]);

  useEffect(() => {
    if (editingCardTitle) {
      cardTitleInputRef.current?.focus();
    }
  }, [editingCardTitle]);

  useEffect(() => {
    if (editingCommentId) {
      commentEditRef.current?.focus();
    }
  }, [editingCommentId]);

  const handleSaveCardTitle = useCallback(() => {
    if (!card) return;
    const trimmed = cardTitleDraft.trim();
    if (trimmed) {
      onUpdateCard(card.id, { title: trimmed });
    }
    setEditingCardTitle(false);
  }, [card, cardTitleDraft, onUpdateCard]);

  const handleCancelCardTitle = useCallback(() => {
    setEditingCardTitle(false);
    if (card) setCardTitleDraft(card.title);
  }, [card]);

  const handleEditDescription = useCallback(() => {
    if (!card) return;
    setDescriptionDraft(card.description ?? '');
    setEditingDescription(true);
  }, [card]);

  const handleSaveDescription = useCallback(() => {
    if (!card) return;
    const trimmed = descriptionDraft.trim();
    onUpdateCard(card.id, {
      description: trimmed || undefined,
    });
    setDescriptionDraft(trimmed);
    setEditingDescription(false);
  }, [card, descriptionDraft, onUpdateCard]);

  const handleBlurDescription = useCallback(() => {
    handleSaveDescription();
  }, [handleSaveDescription]);

  const flushDueToServer = useCallback(() => {
    if (!card) return;
    const trimmed = dueDateLocal.trim();
    if (!trimmed) {
      if (card.dueAt) {
        onUpdateCard(card.id, {
          dueAt: null,
          reminderLeadValue: null,
          reminderLeadUnit: null,
          recurrence: null,
        });
      }
      return;
    }
    onUpdateCard(card.id, {
      dueAt: datetimeLocalToIso(trimmed),
      reminderLeadValue: reminderLeadValueDraft,
      reminderLeadUnit: reminderLeadUnitDraft,
    });
  }, [
    card,
    dueDateLocal,
    reminderLeadValueDraft,
    reminderLeadUnitDraft,
    onUpdateCard,
  ]);

  const flushStartToServer = useCallback(() => {
    if (!card) return;
    const trimmed = startDateLocal.trim();
    if (!trimmed) {
      if (card.startAt) {
        onUpdateCard(card.id, { startAt: null });
      }
      return;
    }
    onUpdateCard(card.id, {
      startAt: datetimeLocalToIso(trimmed),
    });
  }, [card, startDateLocal, onUpdateCard]);

  const handleStartDateBlur = useCallback(() => {
    flushStartToServer();
  }, [flushStartToServer]);

  const handleClearStartDate = useCallback(() => {
    setStartDateLocal('');
    setShowStartDateSection(false);
    if (card?.startAt) {
      onUpdateCard(card.id, {
        startAt: null,
      });
    }
  }, [card, onUpdateCard]);

  const handleDueDateBlur = useCallback(() => {
    flushDueToServer();
  }, [flushDueToServer]);

  const handleReminderLeadBlur = useCallback(() => {
    if (!card) return;
    if (!dueDateLocal.trim() && !card.dueAt) return;
    flushDueToServer();
  }, [card, dueDateLocal, flushDueToServer]);

  const handleReminderLeadUnitChange = useCallback(
    (unit: 'days' | 'hours' | 'minutes' | 'seconds') => {
      if (!card) return;
      setReminderLeadUnitDraft(unit);
      if (!dueDateLocal.trim() && !card.dueAt) return;
      const iso = dueDateLocal.trim()
        ? datetimeLocalToIso(dueDateLocal.trim())
        : card.dueAt!;
      onUpdateCard(card.id, {
        dueAt: iso,
        reminderLeadValue: reminderLeadValueDraft,
        reminderLeadUnit: unit,
      });
    },
    [card, dueDateLocal, reminderLeadValueDraft, onUpdateCard],
  );

  const handleClearDueDate = useCallback(() => {
    setDueDateLocal('');
    setRecurrencePreset('none');
    setShowDueDateSection(false);
    if (card?.dueAt) {
      onUpdateCard(card.id, {
        dueAt: null,
        reminderLeadValue: null,
        reminderLeadUnit: null,
        recurrence: null,
      });
    }
  }, [card, onUpdateCard]);

  const effectiveHasDue = Boolean(
    dueDateLocal.trim() || (card?.dueAt != null && card.dueAt !== ''),
  );

  const applyRecurrencePreset = useCallback(
    (preset: RecurrencePresetId) => {
      if (!card) return;
      setRecurrencePreset(preset);
      if (preset === 'none') {
        if (card.recurrence) {
          onUpdateCard(card.id, { recurrence: null });
        }
        return;
      }
      if (!effectiveHasDue) return;
      const rule = presetToRecurrence(preset);
      onUpdateCard(card.id, { recurrence: rule });
    },
    [card, effectiveHasDue, onUpdateCard],
  );

  const handleRecurrencePresetSelect = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      applyRecurrencePreset(e.target.value as RecurrencePresetId);
    },
    [applyRecurrencePreset],
  );

  const handleScheduleNextDue = useCallback(() => {
    if (!card?.recurrence || !effectiveHasDue) return;
    onUpdateCard(card.id, { advanceDueToNextRecurrence: true });
  }, [card, effectiveHasDue, onUpdateCard]);

  const uploadCommentAttachments = useCallback(
    async (files: File[]): Promise<KanbanCardCommentAttachment[]> => {
      if (!files.length || !boardId || !card?.id || !companyId) return [];

      const inferMimeType = (file: File): string => {
        if (file.type) return file.type;
        const lower = file.name.toLowerCase();
        if (lower.endsWith('.png')) return 'image/png';
        if (lower.endsWith('.jpg') || lower.endsWith('.jpeg'))
          return 'image/jpeg';
        if (lower.endsWith('.gif')) return 'image/gif';
        if (lower.endsWith('.webp')) return 'image/webp';
        if (lower.endsWith('.webm')) return 'video/webm';
        if (lower.endsWith('.mp4')) return 'video/mp4';
        if (lower.endsWith('.ogg')) return 'video/ogg';
        if (lower.endsWith('.mov')) return 'video/quicktime';
        if (lower.endsWith('.m4v')) return 'video/x-m4v';
        return 'application/octet-stream';
      };

      const attachmentFiles = files.filter((f) => {
        if (f.type.startsWith('image/') || f.type.startsWith('video/'))
          return true;
        return /\.(webm|mp4|ogg|mov|m4v|png|jpe?g|gif|webp)$/i.test(f.name);
      });
      if (!attachmentFiles.length) return [];

      try {
        const urlResults = await Promise.all(
          attachmentFiles.map((file) => {
            const mimeType = inferMimeType(file);
            return getCommentAttachmentUploadUrl(
              boardId,
              card.id,
              {
                filename: file.name,
                contentType: mimeType,
                expiresInSeconds: 3600,
              },
              companyId,
            );
          }),
        );

        const putResults = await Promise.all(
          attachmentFiles.map((file, i) => {
            const { uploadUrl } = urlResults[i];
            const mimeType = inferMimeType(file);
            return fetch(uploadUrl, {
              method: 'PUT',
              body: file,
              headers: {
                'Content-Type': mimeType,
              },
            });
          }),
        );

        const failedPut = putResults.find((r) => !r.ok);
        if (failedPut) {
          let details = '';
          try {
            details = (await failedPut.text()).trim();
          } catch {
            // ignore body parse failures
          }
          const suffix = details
            ? ` (${failedPut.status} ${failedPut.statusText}: ${details.slice(0, 240)})`
            : ` (${failedPut.status} ${failedPut.statusText})`;
          throw new Error(`Upload to storage failed${suffix}.`);
        }

        return attachmentFiles.map((file, i) => {
          const mimeType = inferMimeType(file);
          return {
            path: urlResults[i].path,
            filename: file.name,
            size: file.size,
            mimeType,
          };
        });
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to upload attachment.';
        onCommentUploadError?.(message);
        return [];
      }
    },
    [boardId, card?.id, companyId, onCommentUploadError],
  );

  const handleSubmitComment = useCallback(
    async (body?: string) => {
      const toSubmit = (body ?? commentText).trim();
      const hasContent = toSubmit || pendingCommentFiles.length > 0;
      if (!card || !hasContent) return;
      if (isSubmittingCommentRef.current) return;
      isSubmittingCommentRef.current = true;
      setIsSubmittingComment(true);
      try {
        let attachments: KanbanCardCommentAttachment[] = [];
        if (pendingCommentFiles.length > 0) {
          attachments = await uploadCommentAttachments(pendingCommentFiles);
          setPendingCommentFiles([]);
        }
        let textWithoutImages = stripCommentHtmlImages(toSubmit).trim();
        if (!textWithoutImages && attachments.length > 0) {
          textWithoutImages = '\u00A0';
        }
        if (!textWithoutImages && attachments.length === 0) {
          return;
        }
        onAddComment(
          card.id,
          sanitizeCommentHtml(textWithoutImages),
          attachments,
        );
        setCommentText('');
      } catch {
        // uploadCommentAttachments already shows toast on error
      } finally {
        isSubmittingCommentRef.current = false;
        setIsSubmittingComment(false);
      }
    },
    [
      card,
      commentText,
      onAddComment,
      pendingCommentFiles,
      uploadCommentAttachments,
    ],
  );

  const handleSaveEditComment = useCallback(
    (commentId: string, text?: string) => {
      if (!card?.comments) return;
      const trimmed = (text ?? editingCommentText).trim();
      if (!trimmed) return;
      const next = card.comments.map((c) =>
        c.id === commentId ? { ...c, text: trimmed } : c,
      );
      onUpdateCard(card.id, { comments: next });
      setEditingCommentId(null);
      setEditingCommentText('');
    },
    [card, editingCommentText, onUpdateCard],
  );

  const handleDeleteComment = useCallback(
    (commentId: string) => {
      if (!card?.comments) return;
      const commentToDelete = card.comments.find((c) => c.id === commentId);
      if (!commentToDelete) return;
      const remainingComments = card.comments.filter((c) => c.id !== commentId);
      const strippedText = stripCommentHtmlImages(
        commentToDelete.text ?? '',
      ).trim();
      const normalizedText = strippedText.replace(/\s+/g, ' ');
      const truncatedText =
        normalizedText.length > 80
          ? `${normalizedText.slice(0, 77).trimEnd()}...`
          : normalizedText;
      const attachmentCount = commentToDelete.attachments?.length ?? 0;
      const hasDeletedText =
        truncatedText.length > 0 && truncatedText !== '\u00A0';
      const detailParts: string[] = [];
      if (attachmentCount > 0) {
        detailParts.push(
          `${attachmentCount} media attachment${attachmentCount > 1 ? 's' : ''}`,
        );
      }
      const deletedKindLabel =
        commentToDelete.kind === 'activity' ? 'activity item' : 'comment';
      const activityText =
        commentToDelete.kind !== 'activity' &&
        hasDeletedText &&
        attachmentCount === 0
          ? 'deleted a comment'
          : detailParts.length > 0
            ? `deleted ${deletedKindLabel}: ${detailParts.join(', ')}`
            : `deleted ${deletedKindLabel}`;
      const deletionActivity: KanbanCardComment = {
        id: `local-activity-${generateId()}`,
        authorName: commentToDelete.authorName || 'Someone',
        ...(commentToDelete.authorId
          ? { authorId: commentToDelete.authorId }
          : {}),
        kind: 'activity',
        text: activityText,
        createdAt: new Date().toISOString(),
      };
      const next = [...remainingComments, deletionActivity];
      // Keep comments as an array (even empty) to avoid APIs that reject `undefined`.
      onUpdateCard(card.id, { comments: next });
      setEditingCommentId(null);
      setEditingCommentText('');
    },
    [card, onUpdateCard],
  );

  const handlePendingFilesAdded = useCallback((files: File[]) => {
    const allowed = files.filter((f) => {
      if (f.type.startsWith('image/') || f.type.startsWith('video/'))
        return true;
      return /\.(webm|mp4|ogg|mov|m4v|png|jpe?g|gif|webp)$/i.test(f.name);
    });
    setPendingCommentFiles((prev) => [...prev, ...allowed]);
  }, []);

  const handleDeleteChecklist = useCallback(() => {
    if (!card) return;
    onUpdateCard(card.id, { checklist: undefined });
  }, [card, onUpdateCard]);

  const handleToggleChecklistItem = useCallback(
    (itemId: string) => {
      if (!card?.checklist) return;
      const next = card.checklist.map((it) =>
        it.id === itemId ? { ...it, checked: !it.checked } : it,
      );
      onUpdateCard(card.id, { checklist: next });
    },
    [card, onUpdateCard],
  );

  const handleUpdateChecklistItemText = useCallback(
    (itemId: string, text: string) => {
      if (!card?.checklist) return;
      const next = card.checklist.map((it) =>
        it.id === itemId ? { ...it, text } : it,
      );
      onUpdateCard(card.id, { checklist: next });
    },
    [card, onUpdateCard],
  );

  const handleAddChecklistItem = useCallback(() => {
    if (!card || !newChecklistItemText.trim()) return;
    const items = card.checklist ?? [];
    const newItem: ChecklistItem = {
      id: generateId(),
      text: newChecklistItemText.trim(),
      checked: false,
    };
    onUpdateCard(card.id, { checklist: [...items, newItem] });
    setNewChecklistItemText('');
  }, [card, newChecklistItemText, onUpdateCard]);

  const handleRemoveChecklistItem = useCallback(
    (itemId: string) => {
      if (!card?.checklist) return;
      const next = card.checklist.filter((it) => it.id !== itemId);
      onUpdateCard(card.id, { checklist: next.length ? next : undefined });
    },
    [card, onUpdateCard],
  );

  const handleToggleLabel = useCallback(
    (labelId: string) => {
      if (!card) return;
      // Backend requires UUID label ids; do not write optimistic temp labels to the card.
      if (!UUID_REGEX.test(labelId)) return;
      const current = card.labelIds ?? [];
      const next = current.includes(labelId)
        ? current.filter((id) => id !== labelId)
        : [...current, labelId];
      onUpdateCard(card.id, { labelIds: next });
    },
    [card, onUpdateCard],
  );

  const handleToggleMember = useCallback(
    (memberId: string) => {
      if (!card) return;
      const current = card.memberIds ?? [];
      const next = current.includes(memberId)
        ? current.filter((id) => id !== memberId)
        : [...current, memberId];
      onUpdateCard(card.id, { memberIds: next });
    },
    [card, onUpdateCard],
  );

  useEffect(() => {
    if (createLabelModalOpen && availableLabelColors.length > 0) {
      setNewLabelColor((prev: string) =>
        availableLabelColors.includes(prev) ? prev : availableLabelColors[0],
      );
    }
  }, [createLabelModalOpen, availableLabelColors]);

  const handleCreateLabel = useCallback(async () => {
    if (!card || !newLabelName.trim()) return;
    const firstAvailable =
      availableLabelColors.length > 0
        ? availableLabelColors[0]
        : DEFAULT_LABEL_COLORS[0];
    const color = newLabelColor ?? firstAvailable;
    if (editingLabelId) {
      await onUpdateLabel(editingLabelId, { name: newLabelName.trim(), color });
    } else {
      const label: KanbanLabel = {
        id: `label-${generateId()}`,
        name: newLabelName.trim(),
        color,
      };
      const created = await onCreateLabel(label);
      if (created) {
        // Remove any non-UUID ids that might exist due to optimistic UI.
        const safeExisting = (card.labelIds ?? []).filter((id) =>
          UUID_REGEX.test(id),
        );
        const nextLabelIds = safeExisting.includes(created.id)
          ? safeExisting
          : [...safeExisting, created.id];
        onUpdateCard(card.id, {
          labelIds: nextLabelIds,
        });
      }
    }
    setEditingLabelId(null);
    setNewLabelName('');
    setNewLabelColor(
      availableLabelColors.length > 0
        ? availableLabelColors[0]
        : DEFAULT_LABEL_COLORS[0],
    );
    setCreateLabelModalOpen(false);
  }, [
    newLabelName,
    newLabelColor,
    availableLabelColors,
    onCreateLabel,
    onUpdateLabel,
    editingLabelId,
    card,
    onUpdateCard,
  ]);

  const handleRemoveLabelColor = useCallback(() => {
    setNewLabelColor(
      availableLabelColors.length > 0
        ? availableLabelColors[0]
        : DEFAULT_LABEL_COLORS[0],
    );
  }, [availableLabelColors]);

  const comments = useMemo(
    () =>
      card
        ? (card.comments ?? []).filter((comment) => {
            if (comment.kind !== 'activity') return true;
            // Backward-compat cleanup: old generic member activity is ambiguous.
            return comment.text.trim().toLowerCase() !== 'updated members';
          })
        : [],
    [card],
  );
  const commentsNewestFirst = useMemo(
    () =>
      [...comments].sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      ),
    [comments],
  );

  const cardId = card?.id ?? '';
  const currentColumnId =
    card?.listId ??
    (cardId
      ? (columns.find((col) => col.cardIds.includes(cardId))?.id ?? '')
      : '');

  const handleToggleCardMenu = useCallback(
    (target: HTMLElement, initialView: CardMenuView = 'main') => {
      if (!cardId) return;
      const rect = target.getBoundingClientRect();
      setCardMenuAnchor((prev) => {
        if (prev?.cardId === cardId) {
          setCardMenuView(initialView);
          setCloseOnBackFromMoveView(initialView === 'moveToList');
          return null;
        }
        const modalContent = target.closest('.modal-content');
        setCardMenuView(initialView);
        setCloseOnBackFromMoveView(initialView === 'moveToList');
        return {
          cardId,
          currentColumnId,
          rect,
          boundsRect: modalContent?.getBoundingClientRect() ?? null,
        };
      });
    },
    [cardId, currentColumnId],
  );

  if (!card) return null;

  const checklist = card.checklist ?? [];
  const currentColForNext =
    columns.find((c) => c.id === currentColumnId) ?? null;
  const previousCardId = (() => {
    if (!currentColForNext) return null;
    const idx = currentColForNext.cardIds.indexOf(card.id);
    if (idx <= 0) return null;
    return currentColForNext.cardIds[idx - 1] ?? null;
  })();
  const nextCardId = (() => {
    if (!currentColForNext) return null;
    const idx = currentColForNext.cardIds.indexOf(card.id);
    if (idx < 0 || idx >= currentColForNext.cardIds.length - 1) return null;
    return currentColForNext.cardIds[idx + 1] ?? null;
  })();

  const handlePreviousCard = () => {
    if (!previousCardId) return;
    onSelectCard(previousCardId);
  };

  const handleNextCard = () => {
    if (!nextCardId) return;
    onSelectCard(nextCardId);
  };

  const checklistChecked = checklist.filter((i) => i.checked).length;
  const checklistPct =
    checklist.length > 0
      ? Math.round((checklistChecked / checklist.length) * 100)
      : 0;
  const filteredLabels = labelSearch.trim()
    ? boardLabels.filter(
        (l) =>
          UUID_REGEX.test(l.id) &&
          l.name.toLowerCase().includes(labelSearch.trim().toLowerCase()),
      )
    : boardLabels.filter((l) => UUID_REGEX.test(l.id));

  const cardLabelIds = (card.labelIds ?? []).filter((id) =>
    UUID_REGEX.test(id),
  );
  const cardMemberIds = card.memberIds ?? [];
  const assignedMembers = cardMemberIds
    .map((id) => boardMembers.find((m) => m.id === id))
    .filter((m): m is BoardMember => Boolean(m));
  const assignedLabels = cardLabelIds
    .map((id) => boardLabels.find((l) => l.id === id))
    .filter((l): l is KanbanLabel => Boolean(l));

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        className={styles.cardModal}
        size="lg"
        enforceFocus={
          !(labelsPanelOpen || membersPanelOpen || createLabelModalOpen)
        }
      >
        <Modal.Header closeButton className={styles.cardHeader}>
          <button
            type="button"
            className={styles.cardListLabel}
            onClick={(e) => {
              e.stopPropagation();
              handleToggleCardMenu(
                e.currentTarget as HTMLElement,
                'moveToList',
              );
            }}
            aria-label={`Menu for card ${card.title}`}
            aria-expanded={Boolean(cardMenuAnchor)}
          >
            {columnTitle}
          </button>
          <div className={styles.cardHeaderActions}>
            <button
              ref={cardMenuButtonRef}
              type="button"
              className={styles.cardMenuTriggerBtn}
              onClick={(e) => {
                e.stopPropagation();
                handleToggleCardMenu(e.currentTarget as HTMLElement, 'main');
              }}
              aria-label={`Menu for card ${card.title}`}
              aria-expanded={Boolean(cardMenuAnchor)}
            >
              <MoreHorizontal size={16} />
            </button>
          </div>
        </Modal.Header>
        <Modal.Body className={styles.cardBody}>
          <div className={styles.cardTwoCol}>
            <section className={styles.cardLeft}>
              <CardDetailLeftColumn
                card={card}
                previousCardId={previousCardId}
                onPreviousCard={handlePreviousCard}
                nextCardId={nextCardId}
                onNextCard={handleNextCard}
                editingCardTitle={editingCardTitle}
                cardTitleDraft={cardTitleDraft}
                setCardTitleDraft={setCardTitleDraft}
                setEditingCardTitle={setEditingCardTitle}
                cardTitleInputRef={cardTitleInputRef}
                onSaveCardTitle={handleSaveCardTitle}
                onCancelCardTitle={handleCancelCardTitle}
                labelsPanelOpen={labelsPanelOpen}
                onLabelsClick={() => {
                  setLabelsPanelOpen((o) => !o);
                  setMembersPanelOpen(false);
                  setLabelsPanelAnchor(
                    labelsBtnRef.current?.getBoundingClientRect() ?? null,
                  );
                }}
                labelsBtnRef={labelsBtnRef}
                membersPanelOpen={membersPanelOpen}
                onMembersClick={() => {
                  setMembersPanelOpen((o) => !o);
                  setLabelsPanelOpen(false);
                  setMembersPanelAnchor(
                    membersBtnRef.current?.getBoundingClientRect() ?? null,
                  );
                }}
                membersBtnRef={membersBtnRef}
                onAddMemberClick={() => {
                  setMembersPanelOpen(true);
                  setLabelsPanelOpen(false);
                  setMembersPanelAnchor(
                    membersAddBtnRef.current?.getBoundingClientRect() ?? null,
                  );
                }}
                membersAddBtnRef={membersAddBtnRef}
                assignedMembers={assignedMembers}
                assignedLabels={assignedLabels}
                editingDescription={editingDescription}
                descriptionDraft={descriptionDraft}
                setDescriptionDraft={setDescriptionDraft}
                descriptionTextareaRef={descriptionTextareaRef}
                onDescBold={handleDescBold}
                onDescItalic={handleDescItalic}
                onDescStrikethrough={handleDescStrikethrough}
                onDescCode={handleDescCode}
                onDescClearFormatting={handleDescClearFormatting}
                onDescBulletList={handleDescBulletList}
                onDescNumberedList={handleDescNumberedList}
                onEditDescription={handleEditDescription}
                onSaveDescription={handleSaveDescription}
                onBlurDescription={handleBlurDescription}
                onCancelDescription={() => {
                  setEditingDescription(false);
                  setDescriptionDraft(card.description ?? '');
                }}
                showFullDescription={showFullDescription}
                setShowFullDescription={setShowFullDescription}
                checklist={checklist}
                checklistPct={checklistPct}
                newChecklistItemText={newChecklistItemText}
                setNewChecklistItemText={setNewChecklistItemText}
                onToggleChecklistItem={handleToggleChecklistItem}
                onUpdateChecklistItemText={handleUpdateChecklistItemText}
                onRemoveChecklistItem={handleRemoveChecklistItem}
                onAddChecklistItem={handleAddChecklistItem}
                onDeleteChecklist={handleDeleteChecklist}
                showStartDateSection={showStartDateSection}
                onExpandStartDateForm={() => setShowStartDateSection(true)}
                onCollapseStartDateForm={() => setShowStartDateSection(false)}
                startDateLocal={startDateLocal}
                onStartDateLocalChange={setStartDateLocal}
                onStartDateBlur={handleStartDateBlur}
                onClearStartDate={handleClearStartDate}
                showDueDateSection={showDueDateSection}
                onExpandDueDateForm={() => setShowDueDateSection(true)}
                onCollapseDueDateForm={() => setShowDueDateSection(false)}
                dueDateLocal={dueDateLocal}
                onDueDateLocalChange={setDueDateLocal}
                onDueDateBlur={handleDueDateBlur}
                reminderLeadValue={reminderLeadValueDraft}
                onReminderLeadValueChange={setReminderLeadValueDraft}
                onReminderLeadBlur={handleReminderLeadBlur}
                reminderLeadUnit={reminderLeadUnitDraft}
                onReminderLeadUnitChange={handleReminderLeadUnitChange}
                recurrencePreset={recurrencePreset}
                onRecurrencePresetChange={handleRecurrencePresetSelect}
                recurrenceDisabled={!effectiveHasDue}
                recurrenceSummary={
                  card.recurrence ? formatRecurrenceLabel(card.recurrence) : ''
                }
                onScheduleNextDue={handleScheduleNextDue}
                canScheduleNextDue={Boolean(card.recurrence && effectiveHasDue)}
                onClearDueDate={handleClearDueDate}
              />
            </section>
            <section className={styles.cardRight}>
              <CardDetailRightColumn
                boardId={boardId}
                cardId={card?.id ?? null}
                companyId={companyId}
                boardMembers={boardMembers}
                commentIdToScroll={commentIdToScroll}
                onCopyCommentLink={onCopyCommentLink}
                commentText={commentText}
                setCommentText={setCommentText}
                commentEditorActive={commentEditorActive}
                setCommentEditorActive={setCommentEditorActive}
                commentEditorRef={commentEditorRef}
                commentEditRef={commentEditRef}
                editingCommentId={editingCommentId}
                editingCommentText={editingCommentText}
                setEditingCommentId={setEditingCommentId}
                setEditingCommentText={setEditingCommentText}
                onSubmitComment={(body) => void handleSubmitComment(body)}
                onSaveEditComment={handleSaveEditComment}
                onDeleteComment={handleDeleteComment}
                commentsNewestFirst={commentsNewestFirst}
                pendingCommentFiles={pendingCommentFiles}
                onPendingFilesAdded={handlePendingFilesAdded}
                onClearPendingAttachments={() => setPendingCommentFiles([])}
                isSubmittingComment={isSubmittingComment}
              />
            </section>
          </div>
        </Modal.Body>
      </Modal>
      <CardMenu
        anchor={cardMenuAnchor}
        activeColumnId={currentColumnId}
        closeOnBackFromMoveView={closeOnBackFromMoveView}
        activeBoardId={activeBoardId}
        boards={boards}
        view={cardMenuView}
        columns={columns}
        getBoardListsForMove={getBoardListsForMove}
        onClose={() => {
          setCardMenuAnchor(null);
          setCardMenuView('main');
          setCloseOnBackFromMoveView(false);
        }}
        onViewChange={setCardMenuView}
        onRenameCard={() => {
          setEditingCardTitle(true);
          setCardTitleDraft(card.title);
          setCardMenuAnchor(null);
        }}
        onToggleStartDatePanel={(cardId) => {
          if (cardId !== card.id) return;
          setShowStartDateSection((prev) => !prev);
        }}
        isStartDatePanelOpen={showStartDateSection}
        onToggleDueDatePanel={(cardId) => {
          if (cardId !== card.id) return;
          setShowDueDateSection((prev) => !prev);
        }}
        isDueDatePanelOpen={showDueDateSection}
        onCopyCardLink={onCopyCardLink}
        onArchiveCard={(cardId) => {
          void onArchiveCard(cardId);
          setCardMenuAnchor(null);
          setCloseOnBackFromMoveView(false);
          onHide();
        }}
        onMoveCard={(
          cardId,
          targetColumnId,
          targetBoardId,
          targetPosition0Based,
        ) => {
          void onMoveCard(
            cardId,
            targetColumnId,
            targetBoardId,
            targetPosition0Based,
          );
          setCardMenuAnchor(null);
          setCloseOnBackFromMoveView(false);
        }}
      />
      {show && labelsPanelOpen && labelsPanelAnchor && (
        <CardDetailLabelsPanel
          show={show}
          anchor={labelsPanelAnchor}
          labelSearch={labelSearch}
          onLabelSearchChange={setLabelSearch}
          filteredLabels={filteredLabels}
          cardLabelIds={cardLabelIds}
          onToggleLabel={handleToggleLabel}
          onEditLabelClick={(label) => {
            setEditingLabelId(label.id);
            setNewLabelName(label.name);
            setNewLabelColor(label.color);
            setCreateLabelModalOpen(true);
          }}
          onCreateLabelClick={() => {
            setEditingLabelId(null);
            setCreateLabelModalOpen(true);
          }}
          onClose={() => setLabelsPanelOpen(false)}
        />
      )}
      {show && membersPanelOpen && membersPanelAnchor && (
        <CardDetailMembersPanel
          show={show}
          anchor={membersPanelAnchor}
          boardMembers={boardMembers}
          cardMemberIds={cardMemberIds}
          onToggleMember={handleToggleMember}
          onClose={() => setMembersPanelOpen(false)}
        />
      )}
      {show && createLabelModalOpen && (
        <CardDetailCreateLabelModal
          show={show}
          newLabelName={newLabelName}
          newLabelColor={newLabelColor}
          availableLabelColors={availableLabelColors}
          onNewLabelNameChange={setNewLabelName}
          onNewLabelColorChange={setNewLabelColor}
          onRemoveLabelColor={handleRemoveLabelColor}
          onCreateLabel={() => void handleCreateLabel()}
          onDeleteLabel={async () => {
            if (!editingLabelId) return;
            await onDeleteLabel(editingLabelId);
            setCreateLabelModalOpen(false);
            setEditingLabelId(null);
          }}
          isEditMode={Boolean(editingLabelId)}
          onClose={() => {
            setCreateLabelModalOpen(false);
            setEditingLabelId(null);
          }}
          defaultLabelColor={DEFAULT_LABEL_COLORS[0]}
        />
      )}
    </>
  );
}
